import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBackRounded,
  ArrowForwardRounded,
  LockRounded,
  LoginRounded,
  PersonRounded,
  RestaurantRounded,
  SearchRounded,
  StorefrontRounded,
} from '@mui/icons-material';
import api from '../api/api';

const TURNSTILE_SCRIPT_ID = 'integranube-turnstile-script';

function normalizeLoginIdentifier(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const compactRut = raw
    .toUpperCase()
    .replace(/[.\-\s]/g, '')
    .replace(/[^0-9K]/g, '');

  if (!raw.includes('@') && /^[0-9]{7,8}[0-9K]$/.test(compactRut)) {
    const body = compactRut.slice(0, -1).replace(/^0+/, '') || '0';
    return `${body}-${compactRut.slice(-1)}`;
  }

  return raw.toLowerCase();
}

function tenantId(tenant) {
  return tenant?.tenantid || tenant?.tenantId || tenant?.tenant_id || tenant?.id_tenant || tenant?.id || '';
}

function tenantName(tenant) {
  return tenant?.name || tenant?.nombre || tenant?.nombre_fantasia || tenant?.razon_social || tenant?.tenant_name || 'Empresa';
}

function tenantInitials(tenant) {
  const words = tenantName(tenant).split(/\s+/).filter(Boolean);
  return `${words[0]?.[0] || 'I'}${words[1]?.[0] || words[0]?.[1] || 'N'}`.toUpperCase();
}

function commitSession({ token, user, tenant }) {
  const id = tenantId(tenant);

  localStorage.setItem('token', token);
  sessionStorage.setItem('token', token);
  localStorage.setItem('tenantid', id);
  localStorage.setItem('tenant', JSON.stringify(tenant));
  localStorage.setItem('user', JSON.stringify(user || {}));

  api.defaults.headers.common.Authorization = `Bearer ${token}`;
  api.defaults.headers.common.TenantId = id;
}

function clearTemporarySession() {
  sessionStorage.removeItem('token');

  try {
    delete api.defaults.headers.common.Authorization;
    delete api.defaults.headers.common.TenantId;
  } catch (_) {
    // noop
  }
}

function Turnstile({ siteKey, onToken, onError }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({ onToken, onError });

  useEffect(() => {
    callbacksRef.current = { onToken, onError };
  }, [onError, onToken]);

  useEffect(() => {
    let cancelled = false;
    let script = null;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !containerRef.current) return;
      if (widgetIdRef.current != null) return;

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'login',
          theme: 'light',
          size: 'normal',
          callback: (value) => {
            callbacksRef.current.onToken?.(String(value || '').trim());
          },
          'expired-callback': () => {
            callbacksRef.current.onToken?.('');
          },
          'timeout-callback': () => {
            callbacksRef.current.onToken?.('');
          },
          'error-callback': (code) => {
            callbacksRef.current.onToken?.('');
            callbacksRef.current.onError?.(String(code || 'TURNSTILE_ERROR'));
          },
        });
      } catch (error) {
        callbacksRef.current.onToken?.('');
        callbacksRef.current.onError?.(error?.message || 'TURNSTILE_RENDER_FAILED');
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      script = document.getElementById(TURNSTILE_SCRIPT_ID);

      if (!script) {
        script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }

      script.addEventListener('load', renderWidget, { once: true });
      script.addEventListener(
        'error',
        () => callbacksRef.current.onError?.('TURNSTILE_SCRIPT_LOAD_FAILED'),
        { once: true }
      );
    }

    return () => {
      cancelled = true;

      try {
        if (widgetIdRef.current != null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (_) {
        // noop
      }

      widgetIdRef.current = null;
    };
  }, [siteKey]);

  return <Box ref={containerRef} sx={{ minHeight: 65 }} />;
}

export default function LoginForm({ onLogin }) {
  const siteKey = String(process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();

  const [step, setStep] = useState('login');
  const [identifier, setIdentifier] = useState(() => localStorage.getItem('rememberedLogin') || '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => Boolean(localStorage.getItem('rememberedLogin')));
  const [token, setToken] = useState('');
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileVersion, setTurnstileVersion] = useState(0);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();

    return tenants
      .filter((tenant) => tenantId(tenant))
      .filter((tenant) => !q || JSON.stringify(tenant).toLowerCase().includes(q));
  }, [query, tenants]);

  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileVersion((value) => value + 1);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const loginIdentifier = normalizeLoginIdentifier(identifier);

    if (!loginIdentifier || !password) {
      setError('Ingresa correo o RUT y contraseña.');
      return;
    }

    if (!siteKey) {
      setError('Turnstile no está configurado. Defina REACT_APP_TURNSTILE_SITE_KEY y reinicie React.');
      return;
    }

    if (!turnstileToken) {
      setError('Debes completar la verificación de seguridad.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      clearTemporarySession();

      const { data } = await api.post('/auth/login', {
        email: loginIdentifier,
        identifier: loginIdentifier,
        login: loginIdentifier,
        password,
        turnstileToken,
      });

      if (!data?.token) {
        throw new Error('La API no devolvió token.');
      }

      // Token temporal de la pestaña para consultar /auth/me antes de elegir tenant.
      sessionStorage.setItem('token', data.token);
      api.defaults.headers.common.Authorization = `Bearer ${data.token}`;

      const me = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${data.token}` },
      });

      const allowed = Array.isArray(me?.data?.allowedTenants) ? me.data.allowedTenants : [];
      const meUser = me?.data?.user || null;

      if (remember) localStorage.setItem('rememberedLogin', loginIdentifier);
      else localStorage.removeItem('rememberedLogin');

      setToken(data.token);
      setUser(meUser);
      setTenants(allowed);

      if (!allowed.length) {
        throw new Error('Tu usuario no tiene empresas asignadas.');
      }

      if (allowed.length === 1) {
        commitSession({ token: data.token, user: meUser, tenant: allowed[0] });
        await onLogin?.({ token: data.token, user: meUser, tenant: allowed[0] });
        return;
      }

      setStep('tenant');
    } catch (err) {
      clearTemporarySession();
      resetTurnstile();

      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Credenciales incorrectas o error de servidor.'
      );
    } finally {
      setLoading(false);
    }
  };

  const chooseTenant = async (tenant) => {
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      commitSession({ token, user, tenant });
      await onLogin?.({ token, user, tenant });
    } catch (err) {
      setError(err?.message || 'No fue posible seleccionar la empresa.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    clearTemporarySession();
    setStep('login');
    setToken('');
    setUser(null);
    setTenants([]);
    setQuery('');
    setPassword('');
    setError('');
    resetTurnstile();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        p: 2,
        background:
          'radial-gradient(circle at 15% 15%, rgba(55,127,190,.26), transparent 34%), linear-gradient(135deg, #0d2948 0%, #102f50 44%, #0a223c 100%)',
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={18}
          sx={{
            overflow: 'hidden',
            borderRadius: 2.2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '330px minmax(0,1fr)' },
            border: '1px solid rgba(255,255,255,.12)',
          }}
        >
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              minHeight: 570,
              p: 4,
              color: '#fff',
              flexDirection: 'column',
              justifyContent: 'space-between',
              background: 'linear-gradient(160deg, #173f69 0%, #102f50 58%, #0b243f 100%)',
            }}
          >
            <Box>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Box
                  component="img"
                  src="/tavolata-logo"
                  alt="IntegraNube Restaurant"
                  sx={{ width: 280, height: 70, objectFit: 'contain' }}
                />
              </Stack>

              <Typography variant="h5" fontWeight={950} sx={{ mt: 5, lineHeight: 1.18 }}>
                Mesas, comandas, cocina y caja en una sola operación.
              </Typography>
              <Typography sx={{ mt: 1.3, color: 'rgba(255,255,255,.72)', lineHeight: 1.65 }}>
                Acceso seguro al terminal Restaurant de IntegraNube.
              </Typography>
            </Box>

            <Stack spacing={1.1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <RestaurantRounded sx={{ color: '#70b7ff' }} />
                <Typography variant="body2" fontWeight={800}>Mesas y comandas</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <StorefrontRounded sx={{ color: '#70b7ff' }} />
                <Typography variant="body2" fontWeight={800}>Cocina y caja central</Typography>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2.5, sm: 4 }, bgcolor: '#fff' }}>
            <Stack direction="row" spacing={1.1} alignItems="center" sx={{ display: { xs: 'flex', md: 'none' }, mb: 3 }}>
              <Box component="img" src="/favicon-128.png" alt="IntegraNube" sx={{ width: 54, height: 54 }} />
              <Box>
                <Typography fontWeight={950} sx={{ color: '#17385f', fontSize: 21 }}>IntegraNube Restaurant</Typography>
                <Typography variant="caption" color="text.secondary">Mesas · Comandas · Cocina · Caja</Typography>
              </Box>
            </Stack>

            {step === 'login' ? (
              <Box component="form" onSubmit={submit}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="overline" color="primary" fontWeight={950}>Acceso Restaurant</Typography>
                    <Typography variant="h5" fontWeight={950}>Iniciar sesión</Typography>
                    <Typography color="text.secondary" variant="body2" sx={{ mt: 0.35 }}>
                      Ingrese con las mismas credenciales de IntegraNube.
                    </Typography>
                  </Box>

                  <TextField
                    label="Correo o RUT"
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                    autoComplete="username"
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonRounded />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Contraseña"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockRounded />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {siteKey ? (
                    <Box
                      sx={{
                        minHeight: 82,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                        border: '1px solid #d8e2ec',
                        borderRadius: 1.5,
                        bgcolor: '#f8fafc',
                      }}
                    >
                      <Turnstile
                        key={turnstileVersion}
                        siteKey={siteKey}
                        onToken={(value) => {
                          setTurnstileToken(value);
                          if (value) setError('');
                        }}
                        onError={() => {
                          setTurnstileToken('');
                          setError('No se pudo cargar la verificación de seguridad. Recargue la página e intente nuevamente.');
                        }}
                      />
                    </Box>
                  ) : (
                    <Alert severity="error">
                      Falta REACT_APP_TURNSTILE_SITE_KEY en el archivo .env. Reinicie React después de configurarlo.
                    </Alert>
                  )}

                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={remember}
                        onChange={(event) => setRemember(event.target.checked)}
                      />
                    )}
                    label="Recordar usuario"
                  />

                  {error ? <Alert severity="error">{error}</Alert> : null}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading || !siteKey || !turnstileToken}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LoginRounded />}
                    sx={{ minHeight: 50, borderRadius: 1.5, fontWeight: 950 }}
                  >
                    {loading ? 'Validando…' : 'Iniciar sesión'}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="overline" color="primary" fontWeight={950}>Empresa</Typography>
                  <Typography variant="h5" fontWeight={950}>Seleccione dónde operar</Typography>
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 0.35 }}>
                    {user?.name || user?.nombre || user?.email || 'Usuario conectado'}
                  </Typography>
                </Box>

                {tenants.length > 4 ? (
                  <TextField
                    size="small"
                    placeholder="Buscar empresa…"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : null}

                {error ? <Alert severity="error">{error}</Alert> : null}

                <Stack spacing={1.1} sx={{ maxHeight: 390, overflowY: 'auto', pr: 0.4 }}>
                  {options.map((tenant) => (
                    <Card key={tenantId(tenant)} variant="outlined" sx={{ borderRadius: 1.5 }}>
                      <CardActionArea disabled={loading} onClick={() => chooseTenant(tenant)}>
                        <CardContent sx={{ py: '14px !important' }}>
                          <Stack direction="row" spacing={1.4} alignItems="center">
                            <Avatar sx={{ bgcolor: '#1772e8', fontWeight: 950 }}>
                              {tenantInitials(tenant)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography fontWeight={950} noWrap>{tenantName(tenant)}</Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {tenant?.rut || tenant?.rut_empresa || tenantId(tenant)}
                              </Typography>
                            </Box>
                            {loading ? <CircularProgress size={20} /> : <ArrowForwardRounded color="action" />}
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Stack>

                <Button
                  variant="outlined"
                  startIcon={<ArrowBackRounded />}
                  onClick={goBack}
                  disabled={loading}
                  sx={{ borderRadius: 1.5 }}
                >
                  Cambiar cuenta
                </Button>
              </Stack>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
